/**
 * @fileoverview Landing page profit over time chart with mock data.
 * Showcases the ProfitOverTimeChart UI without requiring authentication.
 * @module features/landing/components/LandingProfitChart
 */

import { ReactElement, useMemo, useRef, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Area, AreaChart, CartesianGrid, ReferenceDot, ReferenceLine, XAxis, YAxis } from 'recharts'
import { cn } from '@/lib/utils'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import type { ChartConfig } from '@/components/ui/chart'
import { Card } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { RechartsHoverAdapter } from '@/shared/components/ui/RechartsHoverAdapter'
import { findLabeledExtrema } from '@/shared/utils/chartBars'
import { useIsMobile } from '@/shared/hooks/useIsMobile'

/**
 * Product template for generating daily variations
 */
interface ProductData {
  sku: string
  name: string
  quantity: number
  profit: number
  imageUrl: string
}

/**
 * Base products - we'll vary quantities and profits per day
 */
const BASE_PRODUCTS = {
  monitor: { sku: 'monitor-27', name: 'Monitor 27" 4K', imageUrl: '/landing-page/monitor-mock.webp' },
  keyboard: { sku: 'keyboard-pro', name: 'Mechanical Keyboard Pro', imageUrl: '/landing-page/keyboard-mock.webp' },
  mouse: { sku: 'mouse-gaming', name: 'Gaming Mouse Elite', imageUrl: '/landing-page/mouse-mock.webp' },
  microphone: { sku: 'microphone-usb', name: 'USB Microphone Studio', imageUrl: '/landing-page/microphone-mock.webp' },
  headphones: { sku: 'headphones-hd', name: 'Wireless Headphones HD', imageUrl: '/landing-page/headphones-mock.webp' },
  studioMonitors: { sku: 'studio-monitors', name: 'Studio Monitors 5"', imageUrl: '/landing-page/studiomonitors-mock.webp' },
  controller: { sku: 'controller-pro', name: 'Gaming Controller Pro', imageUrl: '/landing-page/gamingcontroller-mock.webp' },
  powerbank: { sku: 'powerbank-20k', name: 'Power Bank 20000mAh', imageUrl: '/landing-page/powerbank-mock.webp' },
}

/**
 * Daily data with chart profit and per-day product breakdown
 */
interface DayData {
  period: string
  profit: number
  products: {
    profitable: ProductData[]
    losers: ProductData[]
  }
}

/**
 * Mock chart data for 10 days with evolving product performance
 * Each day has 4 profitable and 4 losing products with varying quantities/profits
 */
const MOCK_CHART_DATA: DayData[] = [
  {
    period: '1 Dec',
    profit: 8200,
    products: {
      profitable: [
        { ...BASE_PRODUCTS.monitor, quantity: 3, profit: 5400 },
        { ...BASE_PRODUCTS.keyboard, quantity: 5, profit: 2200 },
        { ...BASE_PRODUCTS.mouse, quantity: 8, profit: 1600 },
        { ...BASE_PRODUCTS.microphone, quantity: 2, profit: 600 },
      ],
      losers: [
        { ...BASE_PRODUCTS.headphones, quantity: 4, profit: -800 },
        { ...BASE_PRODUCTS.studioMonitors, quantity: 2, profit: -400 },
        { ...BASE_PRODUCTS.controller, quantity: 2, profit: -300 },
        { ...BASE_PRODUCTS.powerbank, quantity: 1, profit: -100 },
      ],
    },
  },
  {
    period: '2 Dec',
    profit: 5600,
    products: {
      profitable: [
        { ...BASE_PRODUCTS.keyboard, quantity: 6, profit: 2800 },
        { ...BASE_PRODUCTS.monitor, quantity: 2, profit: 3600 },
        { ...BASE_PRODUCTS.microphone, quantity: 3, profit: 900 },
        { ...BASE_PRODUCTS.mouse, quantity: 4, profit: 800 },
      ],
      losers: [
        { ...BASE_PRODUCTS.controller, quantity: 5, profit: -1200 },
        { ...BASE_PRODUCTS.studioMonitors, quantity: 3, profit: -600 },
        { ...BASE_PRODUCTS.headphones, quantity: 2, profit: -500 },
        { ...BASE_PRODUCTS.powerbank, quantity: 1, profit: -200 },
      ],
    },
  },
  {
    period: '3 Dec',
    profit: -1800,
    products: {
      profitable: [
        { ...BASE_PRODUCTS.mouse, quantity: 12, profit: 2400 },
        { ...BASE_PRODUCTS.microphone, quantity: 2, profit: 600 },
        { ...BASE_PRODUCTS.keyboard, quantity: 1, profit: 400 },
        { ...BASE_PRODUCTS.monitor, quantity: 0, profit: 0 },
      ],
      losers: [
        { ...BASE_PRODUCTS.headphones, quantity: 8, profit: -3200 },
        { ...BASE_PRODUCTS.studioMonitors, quantity: 4, profit: -1200 },
        { ...BASE_PRODUCTS.powerbank, quantity: 3, profit: -500 },
        { ...BASE_PRODUCTS.controller, quantity: 2, profit: -300 },
      ],
    },
  },
  {
    period: '4 Dec',
    profit: 6400,
    products: {
      profitable: [
        { ...BASE_PRODUCTS.monitor, quantity: 4, profit: 7200 },
        { ...BASE_PRODUCTS.keyboard, quantity: 3, profit: 1400 },
        { ...BASE_PRODUCTS.mouse, quantity: 5, profit: 1000 },
        { ...BASE_PRODUCTS.microphone, quantity: 1, profit: 300 },
      ],
      losers: [
        { ...BASE_PRODUCTS.controller, quantity: 6, profit: -1500 },
        { ...BASE_PRODUCTS.headphones, quantity: 3, profit: -900 },
        { ...BASE_PRODUCTS.studioMonitors, quantity: 2, profit: -700 },
        { ...BASE_PRODUCTS.powerbank, quantity: 2, profit: -400 },
      ],
    },
  },
  {
    period: '5 Dec',
    profit: 4200,
    products: {
      profitable: [
        { ...BASE_PRODUCTS.keyboard, quantity: 8, profit: 3800 },
        { ...BASE_PRODUCTS.mouse, quantity: 10, profit: 2000 },
        { ...BASE_PRODUCTS.microphone, quantity: 4, profit: 1200 },
        { ...BASE_PRODUCTS.monitor, quantity: 1, profit: 1800 },
      ],
      losers: [
        { ...BASE_PRODUCTS.studioMonitors, quantity: 5, profit: -1800 },
        { ...BASE_PRODUCTS.powerbank, quantity: 4, profit: -1000 },
        { ...BASE_PRODUCTS.headphones, quantity: 3, profit: -1100 },
        { ...BASE_PRODUCTS.controller, quantity: 2, profit: -700 },
      ],
    },
  },
  {
    // Interior global max — endpoint maxima get extrema labels that clip
    // at the card edge (findLabeledExtrema always labels the global max).
    period: '6 Dec',
    profit: 8600,
    products: {
      profitable: [
        { ...BASE_PRODUCTS.monitor, quantity: 5, profit: 9000 },
        { ...BASE_PRODUCTS.mouse, quantity: 6, profit: 1200 },
        { ...BASE_PRODUCTS.keyboard, quantity: 4, profit: 1800 },
        { ...BASE_PRODUCTS.microphone, quantity: 2, profit: 600 },
      ],
      losers: [
        { ...BASE_PRODUCTS.headphones, quantity: 5, profit: -1600 },
        { ...BASE_PRODUCTS.controller, quantity: 4, profit: -1000 },
        { ...BASE_PRODUCTS.studioMonitors, quantity: 3, profit: -900 },
        { ...BASE_PRODUCTS.powerbank, quantity: 2, profit: -300 },
      ],
    },
  },
  {
    period: '7 Dec',
    profit: 3200,
    products: {
      profitable: [
        { ...BASE_PRODUCTS.keyboard, quantity: 7, profit: 3200 },
        { ...BASE_PRODUCTS.microphone, quantity: 5, profit: 1500 },
        { ...BASE_PRODUCTS.mouse, quantity: 4, profit: 800 },
        { ...BASE_PRODUCTS.monitor, quantity: 1, profit: 1800 },
      ],
      losers: [
        { ...BASE_PRODUCTS.studioMonitors, quantity: 4, profit: -1400 },
        { ...BASE_PRODUCTS.powerbank, quantity: 5, profit: -1200 },
        { ...BASE_PRODUCTS.headphones, quantity: 3, profit: -1000 },
        { ...BASE_PRODUCTS.controller, quantity: 2, profit: -500 },
      ],
    },
  },
  {
    period: '8 Dec',
    profit: -900,
    products: {
      profitable: [
        { ...BASE_PRODUCTS.mouse, quantity: 15, profit: 3000 },
        { ...BASE_PRODUCTS.microphone, quantity: 2, profit: 600 },
        { ...BASE_PRODUCTS.keyboard, quantity: 2, profit: 900 },
        { ...BASE_PRODUCTS.monitor, quantity: 0, profit: 0 },
      ],
      losers: [
        { ...BASE_PRODUCTS.headphones, quantity: 6, profit: -2400 },
        { ...BASE_PRODUCTS.controller, quantity: 4, profit: -1400 },
        { ...BASE_PRODUCTS.studioMonitors, quantity: 3, profit: -1100 },
        { ...BASE_PRODUCTS.powerbank, quantity: 2, profit: -500 },
      ],
    },
  },
  {
    period: '9 Dec',
    profit: 5400,
    products: {
      profitable: [
        { ...BASE_PRODUCTS.monitor, quantity: 3, profit: 5400 },
        { ...BASE_PRODUCTS.keyboard, quantity: 4, profit: 1900 },
        { ...BASE_PRODUCTS.mouse, quantity: 7, profit: 1400 },
        { ...BASE_PRODUCTS.microphone, quantity: 3, profit: 900 },
      ],
      losers: [
        { ...BASE_PRODUCTS.powerbank, quantity: 6, profit: -1800 },
        { ...BASE_PRODUCTS.controller, quantity: 4, profit: -1500 },
        { ...BASE_PRODUCTS.headphones, quantity: 2, profit: -600 },
        { ...BASE_PRODUCTS.studioMonitors, quantity: 1, profit: -300 },
      ],
    },
  },
  {
    period: '10 Dec',
    profit: 6800,
    products: {
      profitable: [
        { ...BASE_PRODUCTS.monitor, quantity: 4, profit: 7200 },
        { ...BASE_PRODUCTS.keyboard, quantity: 5, profit: 2400 },
        { ...BASE_PRODUCTS.microphone, quantity: 3, profit: 900 },
        { ...BASE_PRODUCTS.mouse, quantity: 6, profit: 1200 },
      ],
      losers: [
        { ...BASE_PRODUCTS.headphones, quantity: 5, profit: -2000 },
        { ...BASE_PRODUCTS.studioMonitors, quantity: 4, profit: -1700 },
        { ...BASE_PRODUCTS.controller, quantity: 3, profit: -800 },
        { ...BASE_PRODUCTS.powerbank, quantity: 2, profit: -400 },
      ],
    },
  },
]

/**
 * Chart configuration
 */
const chartConfig: ChartConfig = {
  profit: {
    label: 'Profit / day',
    color: 'var(--color-positive)',
  },
}

/**
 * Formats a number as compact currency
 */
function formatMoney(value: number): string {
  const absValue = Math.abs(value)
  if (absValue >= 1000) {
    return `${(value / 1000).toFixed(1)}K zł`
  }
  return `${value} zł`
}

/**
 * Formats an integer with thousands separator
 */
function formatInt(value: number): string {
  return value.toLocaleString('pl-PL')
}

/**
 * Props for ProductRow component
 */
interface ProductRowProps {
  imageUrl?: string | null
  title: string
  value: string
  valueClassName?: string
  dimQuantityPrefix?: boolean
}

/**
 * Compact product row for tooltip table cells
 */
function ProductRow({
  imageUrl,
  title,
  value,
  valueClassName,
  dimQuantityPrefix = false,
}: ProductRowProps): ReactElement {
  const [imgError, setImgError] = useState(false)
  const shouldFallback = !imageUrl || imgError
  const initials = title ? title.trim().replace(/^\W+/, '').slice(0, 2).toUpperCase() : ''

  const titleParts = dimQuantityPrefix && title.includes(' x ') ? title.split(' x ') : null

  return (
    <div className="flex items-start gap-3">
      <div className="h-[55px] w-[55px] rounded-md bg-muted/20 overflow-hidden shrink-0">
        {shouldFallback ? (
          <div className="w-full h-full flex items-center justify-center text-lg font-medium text-muted-foreground">
            {initials}
          </div>
        ) : (
          <img
            src={imageUrl!}
            alt={title}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        )}
      </div>
      <div className="flex flex-col items-start gap-1 min-w-0">
        <p className="w-full text-xs font-normal leading-snug line-clamp-2 text-muted-foreground">
          {titleParts ? (
            <>
              <span className="text-muted-foreground/75">{titleParts[0]} x</span>
              <span className="text-foreground/90 ml-1">{titleParts[1]}</span>
            </>
          ) : (
            title
          )}
        </p>
        <div className={cn('mt-0.5 font-semibold text-base text-foreground', valueClassName)}>
          {value}
        </div>
      </div>
    </div>
  )
}

/**
 * Props for LandingProfitTooltipContent - the shared tooltip table content
 */
interface LandingProfitTooltipContentProps {
  /** Period string (e.g., "1 Dec") */
  period: string
  /** Total profit for the period */
  totalProfit: number
  /** Translation function */
  t: (key: string) => string
}

/**
 * Reusable tooltip content that displays most/least profitable products for a period.
 * Used by both desktop hover tooltip and mobile bottom sheet.
 */
function LandingProfitTooltipContent({
  period,
  totalProfit,
  t,
}: LandingProfitTooltipContentProps): ReactElement {
  const dayData = MOCK_CHART_DATA.find(d => d.period === period)
  const dayProducts = dayData?.products ?? { profitable: [], losers: [] }

  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow className="hover:bg-transparent border-t-0 bg-black/10">
          <TableHead colSpan={2} className="px-4 h-12 text-center">
            <span className="text-base font-light text-muted-foreground">
              {period}:{' '}
            </span>
            <span
              className={cn(
                'text-base font-semibold',
                totalProfit >= 0 ? 'text-[var(--color-positive)]' : 'text-[var(--color-negative)]'
              )}
            >
              {totalProfit >= 0 ? '+' : ''}
              {formatMoney(Math.round(totalProfit))}
            </span>
          </TableHead>
        </TableRow>
        <TableRow className="hover:bg-transparent border-t-0">
          <TableHead className="w-1/2 px-4 h-8 border-r border-border">
            <span className="text-xs font-medium text-muted-foreground">
              {t('demo.profitChart.mostProfitable')}
            </span>
          </TableHead>
          <TableHead className="w-1/2 px-4 h-8">
            <span className="text-xs font-medium text-muted-foreground">
              {t('demo.profitChart.losingProducts')}
            </span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: Math.max(dayProducts.profitable.length, dayProducts.losers.length) }).map((_, idx) => {
          const leftProduct = dayProducts.profitable[idx]
          const rightProduct = dayProducts.losers[idx]
          return (
            <TableRow key={idx} className="hover:bg-transparent">
              <TableCell className="w-1/2 px-4 py-3 align-top border-r border-border whitespace-normal">
                {leftProduct && (
                  <ProductRow
                    imageUrl={leftProduct.imageUrl}
                    title={`${formatInt(leftProduct.quantity)} × ${leftProduct.name}`}
                    value={`+${formatMoney(Math.round(leftProduct.profit))}`}
                    valueClassName="text-[var(--color-positive)]"
                    dimQuantityPrefix
                  />
                )}
              </TableCell>
              <TableCell className="w-1/2 px-4 py-3 align-top whitespace-normal">
                {rightProduct && (
                  <ProductRow
                    imageUrl={rightProduct.imageUrl}
                    title={`${formatInt(rightProduct.quantity)} × ${rightProduct.name}`}
                    value={formatMoney(Math.round(rightProduct.profit))}
                    valueClassName="text-[var(--color-negative)]"
                    dimQuantityPrefix
                  />
                )}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

/**
 * Custom tooltip props for Recharts
 */
interface LandingTooltipProps {
  active?: boolean
  payload?: { value: number }[]
  label?: string
  /** Translation function */
  t: (key: string) => string
  /** Whether to skip rendering (for mobile where Sheet is used instead) */
  disabled?: boolean
}

/**
 * Custom tooltip component using RechartsHoverAdapter for proper animation handling
 */
function CustomTooltip(
  props: LandingTooltipProps & { [key: string]: unknown }
): ReactElement | null {
  const { t, disabled = false } = props

  const profitValue = props.payload?.[0]?.value ?? 0

  return (
    <RechartsHoverAdapter
      {...props}
      dataChartId="chart-landing-profit"
      width={600}
      estimatedHeight={400}
      followMouse={false}
      disabled={disabled}
    >
      {({ label: tooltipLabel }) => {
        const period = tooltipLabel ? String(tooltipLabel) : ''
        if (!period) return null

        return (
          <LandingProfitTooltipContent
            period={period}
            totalProfit={profitValue}
            t={t}
          />
        )
      }}
    </RechartsHoverAdapter>
  )
}

/**
 * Props for LandingProfitChart component
 */
interface LandingProfitChartProps {
  className?: string
}

/**
 * Landing page profit over time chart with mock data.
 * Mirrors the ProfitOverTimeChart component but uses static mock data.
 *
 * @component
 */
export function LandingProfitChart({ className }: LandingProfitChartProps): ReactElement {
  const { t } = useTranslation('features-landing')
  const isMobile = useIsMobile()

  // Mobile: track selected period for bottom sheet
  const [mobilePeriod, setMobilePeriod] = useState<string | null>(null)
  const [mobileProfitValue, setMobileProfitValue] = useState<number>(0)
  const chartContainerRef = useRef<HTMLDivElement>(null)

  const averageProfit = useMemo(() => {
    const total = MOCK_CHART_DATA.reduce((sum, d) => sum + d.profit, 0)
    return total / MOCK_CHART_DATA.length
  }, [])

  const profitableDays = useMemo(() => {
    const profitable = MOCK_CHART_DATA.filter(d => d.profit > 0).length
    return { profitable, losing: MOCK_CHART_DATA.length - profitable }
  }, [])

  // Label the curve's turning points (local peaks/valleys above a
  // prominence threshold), not just the global peak and trough.
  const extrema = useMemo(
    () => findLabeledExtrema(MOCK_CHART_DATA.map(d => d.profit)),
    []
  )

  // Mobile: handle touch on chart to find nearest data point and open sheet
  const handleMobileTouch = useCallback((clientX: number) => {
    if (!isMobile || !chartContainerRef.current) return

    const rect = chartContainerRef.current.getBoundingClientRect()
    const chartLeft = rect.left + 12 // margin.left
    const chartRight = rect.right - 12 // margin.right
    const chartWidth = chartRight - chartLeft

    // Calculate which data point is closest to the touch
    const touchX = clientX - chartLeft
    const ratio = Math.max(0, Math.min(1, touchX / chartWidth))
    const index = Math.round(ratio * (MOCK_CHART_DATA.length - 1))
    const dataPoint = MOCK_CHART_DATA[index]

    if (dataPoint) {
      setMobilePeriod(dataPoint.period)
      setMobileProfitValue(dataPoint.profit)
    }
  }, [isMobile])

  // Add touch event listener for mobile
  useEffect(() => {
    if (!isMobile) return
    const container = chartContainerRef.current
    if (!container) return

    const handleTouch = (e: TouchEvent): void => {
      const touch = e.touches[0]
      if (e.touches.length === 1 && touch) {
        handleMobileTouch(touch.clientX)
      }
    }

    container.addEventListener('touchstart', handleTouch, { passive: true })
    return () => container.removeEventListener('touchstart', handleTouch)
  }, [isMobile, handleMobileTouch])

  return (
    <Card className={cn('rounded-xl border border-border/40 bg-card/30 px-6 py-6', className)}>
      {/* Header section */}
      <div>
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="flex items-baseline gap-2.5">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('demo.profitChart.profit')}
            </span>
            <span
              className={cn(
                'text-sm font-mono font-semibold',
                averageProfit >= 0 ? 'text-[var(--color-positive)]' : 'text-[var(--color-negative)]'
              )}
            >
              {formatMoney(Math.round(averageProfit))}{t('demo.profitChart.perDay')}
            </span>
          </h3>
          <div className="hidden sm:block text-sm text-muted-foreground/60">
            <span className="text-[var(--color-positive)]/70">
              {profitableDays.profitable} {t('demo.profitChart.profitable')}
            </span>
            <span className="mx-1">/</span>
            <span>
              {profitableDays.losing} {t('demo.profitChart.losing')}
            </span>
          </div>
        </div>
      </div>

      {/* Chart section */}
      <div
        ref={chartContainerRef}
        className="pt-6"
      >
        <ChartContainer
          id="landing-profit"
          config={chartConfig}
          className="h-chart-lg w-full relative z-30"
        >
          <AreaChart
            data={MOCK_CHART_DATA}
            margin={{
              left: 12,
              right: 12,
              // Reserves room for the peak label above a dot that sits
              // exactly at dataMax (the top of the plot area).
              top: 32,
              bottom: 20,
            }}
          >
            <defs>
              <linearGradient id="landingProfitGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-positive)" stopOpacity={0.6} />
                <stop offset="100%" stopColor="var(--color-positive)" stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              vertical={false}
              horizontal={true}
              stroke="var(--color-muted-foreground)"
              strokeOpacity={0.07}
              strokeDasharray="8 4"
              shapeRendering="crispEdges"
            />
            <ReferenceLine
              y={0}
              stroke="var(--color-muted-foreground)"
              strokeOpacity={0.15}
              strokeWidth={1}
            />
            <XAxis
              dataKey="period"
              tickLine={false}
              axisLine={false}
              tickMargin={20}
              tick={{ fontSize: 11, dy: 10 }}
              interval={1}
            />
            <YAxis
              orientation="right"
              tickLine={false}
              axisLine={false}
              tickMargin={13}
              tick={{ fontSize: 12, textAnchor: 'start' }}
              tickFormatter={value => formatMoney(value as number)}
            />
            <ChartTooltip
              cursor={false}
              content={<CustomTooltip t={t} disabled={isMobile} />}
              wrapperStyle={{ display: 'none' }}
            />
            <Area
              dataKey="profit"
              type="monotone"
              fill="url(#landingProfitGradient)"
              stroke="var(--color-positive)"
              strokeWidth={2}
              dot={false}
              activeDot={{
                r: 6,
                stroke: 'var(--color-positive)',
                strokeWidth: 2,
                fill: 'var(--background)',
              }}
              isAnimationActive={false}
            />
            {extrema.map(({ index, kind }) => {
              const point = MOCK_CHART_DATA[index]!
              const isLoss = point.profit < 0
              const dotColor = isLoss ? 'var(--color-negative)' : 'var(--color-positive)'
              // Peaks read as wins (dimmed green above), valleys as context
              // (muted below) — unless the valley is an actual loss (red).
              const labelColor =
                kind === 'max'
                  ? 'oklch(from var(--color-positive) l c h / 0.7)'
                  : isLoss
                    ? 'var(--color-negative)'
                    : 'oklch(from var(--color-muted-foreground) l c h / 0.6)'
              return (
                <ReferenceDot
                  key={`${point.period}-${kind}`}
                  x={point.period}
                  y={point.profit}
                  r={4}
                  fill={dotColor}
                  stroke={dotColor}
                  label={{
                    value: formatMoney(point.profit),
                    position: kind === 'max' ? 'top' : 'bottom',
                    fontSize: 11,
                    fontWeight: 500,
                    fill: labelColor,
                    offset: 10,
                  }}
                />
              )
            })}
          </AreaChart>
        </ChartContainer>
      </div>

      {/* Mobile: Bottom sheet for period details */}
      {isMobile && (
        <Sheet open={!!mobilePeriod} onOpenChange={(open) => !open && setMobilePeriod(null)}>
          <SheetContent
            side="bottom"
            showCloseButton={false}
            className={cn(
              'max-h-[85vh] overflow-y-auto rounded-t-2xl',
              'bg-[var(--glass-surface-dark)]'
            )}
          >
            {/* Hidden title for accessibility */}
            <SheetHeader className="sr-only">
              <SheetTitle>
                {t('demo.profitChart.periodDetails', { defaultValue: 'Period Details' })}
              </SheetTitle>
              <SheetDescription>
                {t('demo.profitChart.periodDetailsDescription', {
                  defaultValue: 'Products breakdown for the selected period',
                })}
              </SheetDescription>
            </SheetHeader>
            <div className="pb-safe">
              {mobilePeriod && (
                <LandingProfitTooltipContent
                  period={mobilePeriod}
                  totalProfit={mobileProfitValue}
                  t={t}
                />
              )}
            </div>
          </SheetContent>
        </Sheet>
      )}
    </Card>
  )
}
